import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import DifferenceIcon from '@mui/icons-material/Difference';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useFeatureFlags } from '../../features/FeatureFlagsContext';
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader';
import { EntityStatGrid } from '../../components/workspace/EntityPanels';
import {
  WorkspaceEmptyState,
  WorkspaceErrorState,
  WorkspaceLoadingState,
} from '../../components/workspace/WorkspaceStates';
import {
  formatDate,
  labelize,
  publicationHealth,
  StatusChip,
} from '../../components/workspace/WorkspaceStatus';
import { canonicalRequest, SectionCard } from './CanonicalEditorCommon';
import {
  managedFields,
  parseListingIDs,
  publicationFieldGroups,
  publicationRows,
  usablePlan,
} from './reconciliationWorkflow';

const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  p: 0,
  m: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

async function mapWithConcurrency(items, concurrency, mapper) {
  const results = new Array(items.length);
  let cursor = 0;
  async function worker() {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      try {
        results[index] = { status: 'fulfilled', value: await mapper(items[index], index) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
  return results;
}

const CanonicalReconcilePage = () => {
  const navigate = useNavigate();
  const { fetchWithAuth } = useAuth();
  const { flags } = useFeatureFlags();
  const [searchParams] = useSearchParams();
  const listingIDs = React.useMemo(() => parseListingIDs(searchParams.get('listing_ids')), [searchParams]);
  const [listings, setListings] = React.useState([]);
  const [selected, setSelected] = React.useState(new Set());
  const [plans, setPlans] = React.useState({});
  const [workflows, setWorkflows] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState('');
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');
  const [loadFailures, setLoadFailures] = React.useState([]);

  const load = React.useCallback(async () => {
    if (!listingIDs.length) {
      setListings([]);
      return;
    }
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const results = await mapWithConcurrency(listingIDs, 6, async (id) => {
        const payload = await canonicalRequest(fetchWithAuth, `/listings/${id}`);
        return payload.item;
      });
      const loaded = results.filter((result) => result.status === 'fulfilled').map((result) => result.value);
      const failures = results
        .map((result, index) => ({ ...result, id: listingIDs[index] }))
        .filter((result) => result.status === 'rejected');
      setListings(loaded);
      setLoadFailures(failures);
      const ids = publicationRows(loaded).map((publication) => publication.id);
      setSelected(new Set(ids));
      setPlans({});
      setWorkflows({});
      if (!loaded.length) setError('None of the selected Listings could be loaded.');
    } catch (loadError) {
      setError(loadError.message || 'Unable to prepare the comparison.');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, listingIDs]);

  React.useEffect(() => {
    load();
  }, [load]);

  const rows = React.useMemo(() => publicationRows(listings), [listings]);
  const missingDestinations = listings.filter((listing) => !(listing.publications || []).length);
  const selectedRows = rows.filter((row) => selected.has(row.id));
  const allSelected = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && !allSelected;
  const readyPlans = selectedRows.filter((row) => usablePlan(plans[row.id]) && workflows[row.id]?.status !== 'started');

  const toggle = (id) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((row) => row.id)));

  const preview = async () => {
    if (!selectedRows.length) return;
    setBusy('planning');
    setError('');
    setNotice('');
    const next = { ...plans };
    for (const publication of selectedRows) {
      next[publication.id] = { status: 'planning' };
      setPlans({ ...next });
      try {
        const plan = await canonicalRequest(fetchWithAuth, `/publications/${publication.id}/plan`, {
          method: 'POST',
          body: JSON.stringify({ expires_in_seconds: 1800 }),
        });
        next[publication.id] = { status: 'planned', plan };
      } catch (planError) {
        next[publication.id] = {
          status: 'failed',
          error: planError.message || 'Unable to prepare a reconciliation plan.',
          validation: planError.validation,
        };
      }
      setPlans({ ...next });
    }
    setBusy('');
    setNotice('Plan preview complete. Inspect the exact actions below before starting any workflow.');
  };

  const apply = async () => {
    if (!readyPlans.length) return;
    setBusy('applying');
    setError('');
    setNotice('');
    const next = { ...workflows };
    for (const publication of readyPlans) {
      const plan = plans[publication.id].plan;
      next[publication.id] = { status: 'starting' };
      setWorkflows({ ...next });
      try {
        const workflow = await canonicalRequest(fetchWithAuth, `/publication-plans/${plan.id}/apply`, {
          method: 'POST',
          body: JSON.stringify({ plan_hash: plan.plan_hash }),
        });
        next[publication.id] = { status: 'started', workflow };
      } catch (applyError) {
        next[publication.id] = { status: 'failed', error: applyError.message || 'Unable to start the workflow.' };
      }
      setWorkflows({ ...next });
    }
    setBusy('');
    setNotice('Accepted plans now have durable workflows. Failures remain visible and can be retried safely.');
  };

  if (!listingIDs.length) {
    return (
      <Stack spacing={2.5}>
        <WorkspacePageHeader title="Compare and reconcile" breadcrumbs={[{ label: 'Listings', to: '/listings' }, { label: 'Compare and reconcile' }]} />
        <WorkspaceEmptyState
          title="No Listings selected"
          description="Select Listings in either Cards or Table view, then choose Compare and reconcile."
          action={<Button variant="contained" onClick={() => navigate('/listings')}>Choose Listings</Button>}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5} sx={{ pb: 4 }}>
      <WorkspacePageHeader
        eyebrow="Plan-first bulk operation"
        title="Compare and reconcile"
        description="Compare each Listing destination, preview immutable plans, and start durable repair workflows only after reviewing their exact scope."
        breadcrumbs={[{ label: 'Listings', to: '/listings' }, { label: 'Compare and reconcile' }]}
        actions={(
          <>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/listings')}>Listings</Button>
            <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />} onClick={load} disabled={loading || Boolean(busy)}>Reload</Button>
          </>
        )}
      />

      <Alert severity="info">
        Previewing reads local desired state and writes an expiring plan only. Starting a reviewed plan creates a durable, throttled workflow; it does not send an untracked burst of provider requests.
      </Alert>
      {error ? <WorkspaceErrorState message={error} onRetry={load} /> : null}
      {notice ? <Alert severity="success" role="status" aria-live="polite">{notice}</Alert> : null}
      <Box component="span" role="status" aria-live="polite" sx={visuallyHidden}>
        {busy === 'planning' ? 'Preparing reconciliation plans.' : busy === 'applying' ? 'Starting reconciliation workflows.' : ''}
      </Box>
      {loadFailures.length ? <Alert severity="warning">{loadFailures.length} of {listingIDs.length} selected Listings could not be loaded. The available Listings are shown.</Alert> : null}
      {loading ? <WorkspaceLoadingState label="Loading Listings and destinations…" /> : null}

      {!loading && listings.length ? (
        <>
          <EntityStatGrid items={[
            { label: 'Listings', value: listings.length, helper: `${missingDestinations.length} without a destination` },
            { label: 'Destinations', value: rows.length, helper: `${new Set(rows.map((row) => row.provider)).size} providers` },
            { label: 'Needs attention', value: rows.filter((row) => publicationHealth(row) === 'needs_attention').length, helper: 'Unverified or failed' },
            { label: 'Plans ready', value: readyPlans.length, helper: `${Object.values(plans).filter((entry) => entry.status === 'failed').length} planning failures` },
          ]} />

          {missingDestinations.length ? (
            <Alert severity="warning">
              {missingDestinations.length} Listing{missingDestinations.length === 1 ? ' has' : 's have'} no sales-channel destination: {missingDestinations.slice(0, 5).map((item) => item.title).join(', ')}{missingDestinations.length > 5 ? '…' : ''}. Add a Publication from each Listing before reconciling it.
            </Alert>
          ) : null}

          <SectionCard
            title="Destination comparison"
            description="A check means PODBoss owns and reconciles that field group for this destination."
            action={(
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button variant="outlined" startIcon={<DifferenceIcon />} onClick={preview} disabled={!flags.publication_workflows || !selected.size || Boolean(busy)}>
                  {busy === 'planning' ? 'Planning…' : `Preview ${selected.size} plan${selected.size === 1 ? '' : 's'}`}
                </Button>
                <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={apply} disabled={!flags.publication_workflows || !readyPlans.length || Boolean(busy)}>
                  {busy === 'applying' ? 'Starting…' : `Start ${readyPlans.length} workflow${readyPlans.length === 1 ? '' : 's'}`}
                </Button>
              </Stack>
            )}
          >
            {!flags.publication_workflows ? <Alert severity="warning">Publication workflows are disabled in this environment.</Alert> : null}
            <TableContainer sx={{ maxHeight: 'min(640px, 65vh)', border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Table stickyHeader size="small" aria-label="Cross-channel publication comparison" sx={{ minWidth: 1260 }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox checked={allSelected} indeterminate={someSelected} onChange={toggleAll} inputProps={{ 'aria-label': allSelected ? 'Deselect all destinations' : 'Select all destinations' }} />
                    </TableCell>
                    <TableCell>Listing</TableCell>
                    <TableCell>Destination</TableCell>
                    <TableCell>Health</TableCell>
                    <TableCell>Remote ID</TableCell>
                    {publicationFieldGroups.map((field) => <TableCell key={field} align="center">{labelize(field)}</TableCell>)}
                    <TableCell>Verified</TableCell>
                    <TableCell>Plan</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((publication) => {
                    const fields = managedFields(publication);
                    const planEntry = plans[publication.id];
                    return (
                      <TableRow key={publication.id} selected={selected.has(publication.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selected.has(publication.id)} onChange={() => toggle(publication.id)} inputProps={{ 'aria-label': `${selected.has(publication.id) ? 'Deselect' : 'Select'} ${publication.listing.title} on ${publication.connection?.display_name || publication.provider}` }} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 240 }}>
                          <Typography variant="body2">{publication.listing.title || 'Untitled Listing'}</Typography>
                          <Typography variant="caption" color="text.secondary">{publication.listing.product?.name}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <Typography variant="body2">{publication.connection?.display_name || labelize(publication.provider)}</Typography>
                          <Typography variant="caption" color="text.secondary">{labelize(publication.provider)}</Typography>
                        </TableCell>
                        <TableCell><StatusChip status={publicationHealth(publication)} /></TableCell>
                        <TableCell sx={{ overflowWrap: 'anywhere' }}>{publication.external_id || 'Not linked'}</TableCell>
                        {publicationFieldGroups.map((field) => (
                          <TableCell key={field} align="center">
                            {fields.includes(field) ? (
                              <><CheckIcon color="success" fontSize="small" aria-hidden="true" /><Box component="span" sx={visuallyHidden}>{labelize(field)} managed</Box></>
                            ) : <Typography component="span" color="text.disabled" aria-label={`${labelize(field)} observed only`}>—</Typography>}
                          </TableCell>
                        ))}
                        <TableCell sx={{ minWidth: 170 }}>
                          <Typography variant="caption" color={publication.last_error ? 'error' : 'text.secondary'}>{publication.last_error || formatDate(publication.last_verified_at)}</Typography>
                        </TableCell>
                        <TableCell><StatusChip status={planEntry?.status || 'draft'} label={planEntry?.status === 'planned' ? 'Ready' : labelize(planEntry?.status || 'not planned')} /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>

          {selectedRows.some((row) => plans[row.id]) ? (
            <SectionCard title="Plan review" description="These actions are immutable for the captured desired version. Re-preview after changing a Listing or Publication.">
              {selectedRows.filter((row) => plans[row.id]).map((publication) => {
                const entry = plans[publication.id];
                const workflowEntry = workflows[publication.id];
                return (
                  <Paper key={publication.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.25}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
                        <Box>
                          <Typography variant="subtitle2">{publication.listing.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{publication.connection?.display_name || labelize(publication.provider)}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <StatusChip status={workflowEntry?.status || entry.status} />
                          {workflowEntry?.workflow?.workflow_run_id ? <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate(`/activity?kind=workflow&q=${workflowEntry.workflow.workflow_run_id}`)}>Activity</Button> : null}
                        </Stack>
                      </Stack>
                      {entry.error ? <Alert severity="error">{entry.error}</Alert> : null}
                      {workflowEntry?.error ? <Alert severity="error">{workflowEntry.error}</Alert> : null}
                      {entry.plan ? (
                        <>
                          <Typography variant="caption" color="text.secondary">Plan {entry.plan.plan_hash?.slice(0, 12)}… expires {formatDate(entry.plan.expires_at)}</Typography>
                          {(entry.plan.warnings || []).map((warning) => <Alert key={`${warning.code}-${warning.field}`} severity="warning">{warning.message}</Alert>)}
                          <Box component="ol" sx={{ my: 0, pl: 3 }}>
                            {(entry.plan.plan?.actions || []).map((action) => <li key={action.key}><Typography variant="body2">{action.description}</Typography></li>)}
                          </Box>
                          {!entry.plan.plan?.actions?.length ? <Alert severity="success">No remote changes are required; verification may still run.</Alert> : null}
                        </>
                      ) : null}
                    </Stack>
                  </Paper>
                );
              })}
            </SectionCard>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
};

export default CanonicalReconcilePage;
